/*! mdv-colour-swap.js - Maniere De Voir PDP in-place colour switch.  MARKER: mdvColourSwap-1.1
 *
 *  UK canary, 2026-10-08. Frozen contract + Amendment 1:
 *  staging-20261008-uk-colour-speed/CONTRACT.md
 *
 *  Tapping a sibling colour card swaps the page to that product in place.
 *    HTML source : Section Rendering API (?section_id=<this section's id>), prefetched
 *                  for the rendered siblings after window load (<= 12, 2 at a time,
 *                  idle-gated, skipped on Save-Data / 2g).
 *    JS state    : /products/<handle>.js
 *  The section's inline scripts use top-level const, so they can never be re-run.
 *  The commit therefore transplants regions and calls explicit re-init hooks that
 *  the theme exposes on window. No <script> or <link> from fetched HTML is ever
 *  inserted. Any doubt (fallback rules F1-F17, failed validation, any exception)
 *  means a normal page load of the card's URL.
 *
 *  Kill switch: ENABLED below (Stage A false, Stage B true).
 *  Per-browser override: ?mdvswap=1 or ?mdvswap=0, persisted in
 *  localStorage 'mdvColourSwap'; the stored value wins over ENABLED.
 */
(function () {
  'use strict';

  var MARKER = 'mdvColourSwap-1.1';
  var ENABLED = true;
  var TTL_MS = 15 * 60e3;
  var CONCURRENCY = 2;
  var MAX_PREFETCH = 12;
  var WAIT_INFLIGHT_MS = 1000;
  var WAIT_HERO_MS = 600;
  var FETCH_TIMEOUT_MS = 8000;
  var LS_KEY = 'mdvColourSwap';
  var NOTIFY_CAPTION = 'Notify Me When Available';
  var MEDIA_BLOCK = '.main_html_mp4_video, .main_external_video, .img_model';

  /* handle -> {status:'idle'|'loading'|'ready'|'failed', doc, product, hero, heroImg, at, promise} */
  var cache = new Map();
  var queue = [];
  var budgeted = new Set();
  var active = 0;
  var maxActive = 0;
  var st = {
    enabled: false, sid: null, handle: null, swapped: false, bulkStarted: false, bulkAllowed: true,
    tapSeq: 0, recsSeq: 0, stampedExpect: null, stampedRetries: 0, section: null, optionSelectors: null,
    selectorInstances: [], prevHandle: null
  };
  /* Parsed sections kept in memory: the sibling budget plus the colour the shopper
     came from (exempt from MAX_PREFETCH so Back / return taps stay in place). */
  var MAX_RETAINED = MAX_PREFETCH + 1;
  var exemptFetches = 0;

  /* ------------------------------------------------------------------ helpers */

  function q(sel, root) { return (root || document).querySelector(sel); }
  function qa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  /* PDP lookups are scoped to the product section. Other page components reuse PDP
     ids and classes: wishlist drawer items (templates/product.ws-pro-list*.liquid)
     carry id="variant-listbox" (div + select), .addToCartBtn and #ws-fav-btn, and the
     drawer precedes the PDP in the DOM. Never query PDP regions page-wide. */
  function P(sel) { return (st.section || document).querySelector(sel); }
  function PA(sel) { return qa(sel, st.section || document); }
  function inPdp(el) { return !!(el && st.section && st.section.contains(el)); }
  var PDP_SELECT = '.product-page--cart-form form .variants select#variant-listbox';
  function pdpSelect() { return P(PDP_SELECT); }
  function now() { return (window.performance && performance.now) ? performance.now() : Date.now(); }
  function delay(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function within(p, ms) { return Promise.race([Promise.resolve(p), delay(ms)]); }
  function mark(name) { try { performance.mark(name); } catch (e) {} }

  function handleFromPath(path) {
    var m = /\/products\/([^\/?#]+)/.exec(path || '');
    if (!m) return null;
    try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
  }

  function cardHandle(card) {
    if (!card) return null;
    var h = card.getAttribute('data-itemhandle');
    if (!h) {
      var a = card.querySelector('a[href]');
      h = a ? handleFromPath(a.getAttribute('href')) : null;
    }
    return h || null;
  }

  /* Liquid `escape` equivalent, for globals the section seeds with | escape. */
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function hasTag(p, tag) { return !!(p && Array.isArray(p.tags) && p.tags.indexOf(tag) !== -1); }

  function firstAvailable(p) {
    var v = (p.variants || []).filter(function (x) { return x.available; })[0];
    return v || p.variants[0];
  }

  function isStale(e) { return !e.at || (Date.now() - e.at) > TTL_MS; }

  /* /products/<h>.js gives options as objects; {{ product | json }} gives names. */
  function normalise(product) {
    var p = JSON.parse(JSON.stringify(product));
    if (Array.isArray(p.options)) {
      p.options = p.options.map(function (o) { return (o && typeof o === 'object') ? o.name : o; });
    }
    return p;
  }

  function stripVariant(href) {
    try {
      var u = new URL(href, location.href);
      if (!u.searchParams.has('variant')) return u.pathname + u.search + u.hash;
      u.searchParams.delete('variant');
      return u.pathname + u.search + u.hash;
    } catch (e) { return href; }
  }

  function isEnabled() {
    var m = /[?&]mdvswap=([01])(?:[&#]|$)/.exec(location.search);
    var v = null;
    try {
      if (m) localStorage.setItem(LS_KEY, m[1]);
      v = localStorage.getItem(LS_KEY);
    } catch (e) {
      v = m ? m[1] : null;
    }
    if (v === '1') return true;
    if (v === '0') return false;
    return ENABLED;
  }

  /* --------------------------------------------------------- DOM transplant */

  function sanitise(node, keepStyle) {
    if (!node || node.nodeType !== 1) return node;
    qa('script, link', node).forEach(function (n) { n.parentNode.removeChild(n); });
    if (!keepStyle) qa('style', node).forEach(function (n) { n.parentNode.removeChild(n); });
    return node;
  }

  function importEl(src, keepStyle) {
    return sanitise(document.importNode(src, true), keepStyle);
  }

  function importChildren(srcParent, keepStyle) {
    var frag = document.createDocumentFragment();
    Array.prototype.forEach.call(srcParent.childNodes, function (c) {
      if (c.nodeType === 1 && c.matches('script, link')) return;
      if (c.nodeType === 1 && !keepStyle && c.matches('style')) return;
      frag.appendChild(c.nodeType === 1 ? importEl(c, keepStyle) : document.importNode(c, true));
    });
    return frag;
  }

  function replaceChildrenFrom(live, src, keepStyle) {
    while (live.firstChild) live.removeChild(live.firstChild);
    live.appendChild(importChildren(src, keepStyle));
  }

  function directChild(parent, sel) {
    if (!parent) return null;
    for (var i = 0; i < parent.children.length; i++) {
      if (parent.children[i].matches(sel)) return parent.children[i];
    }
    return null;
  }

  /* Make the live parent's direct child matching sel mirror the fetched one. */
  function syncPart(live, src, sel, place, keepStyle) {
    var l = directChild(live, sel);
    var s = directChild(src, sel);
    if (s) {
      var n = importEl(s, keepStyle);
      if (l) l.parentNode.replaceChild(n, l); else place(n);
      return n;
    }
    if (l) l.parentNode.removeChild(l);
    return null;
  }

  function copyAttrs(live, src, names) {
    if (!live || !src) return;
    names.forEach(function (n) {
      if (src.hasAttribute(n)) live.setAttribute(n, src.getAttribute(n)); else live.removeAttribute(n);
    });
  }

  function setClass(live, src, name) {
    if (live && src) live.classList.toggle(name, src.classList.contains(name));
  }

  /* ------------------------------------------------------------- prefetch */

  function fetchWithTimeout(url, as) {
    var ctl = ('AbortController' in window) ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctl) ctl.abort(); }, FETCH_TIMEOUT_MS);
    return fetch(url, {
      credentials: 'same-origin',
      signal: ctl ? ctl.signal : undefined,
      headers: as === 'json' ? { Accept: 'application/json' } : undefined
    }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return as === 'json' ? r.json() : r.text();
    }).then(function (v) { clearTimeout(timer); return v; }, function (err) { clearTimeout(timer); throw err; });
  }

  function heroImgIn(doc) {
    var desktop = window.matchMedia && window.matchMedia('(min-width: 768px)').matches;
    return desktop
      ? doc.querySelector('.product-page--images-container #thumbnail-item-1 img')
      : doc.querySelector('.product-page-mobile--images-container .main-litght-img-mobile img');
  }

  /* Decode the hero off-DOM with the same sizes/srcset so the in-DOM copy is a cache hit. */
  function preloadHero(srcImg) {
    if (!srcImg) return { img: null, ready: Promise.resolve() };
    var img = new Image();
    var sizes = srcImg.getAttribute('sizes');
    var srcset = srcImg.getAttribute('srcset');
    var src = srcImg.getAttribute('src');
    if (sizes) img.sizes = sizes;
    if (srcset) img.srcset = srcset;
    if (src) img.src = src;
    var ready = (typeof img.decode === 'function')
      ? img.decode()
      : new Promise(function (r) { img.onload = img.onerror = r; });
    return { img: img, ready: ready.catch(function () {}) };
  }

  function load(h) {
    var e = cache.get(h);
    if (!e) { e = { status: 'idle', handle: h }; cache.set(h, e); }
    if (e.status === 'loading') return e.promise;
    e.status = 'loading';
    var base = '/products/' + encodeURIComponent(h);
    e.promise = Promise.all([
      fetchWithTimeout(base + '?section_id=' + encodeURIComponent(st.sid), 'text'),
      fetchWithTimeout(base + '.js', 'json')
    ]).then(function (res) {
      var doc = new DOMParser().parseFromString(res[0], 'text/html');
      var hero = preloadHero(heroImgIn(doc));
      e.doc = doc;
      e.product = res[1];
      e.heroImg = hero.img;
      e.hero = hero.ready;
      e.at = Date.now();
      e.used = e.at;
      e.status = 'ready';
      evictBeyondCap();
    }, function (err) {
      e.status = 'failed';
      e.error = String((err && err.message) || err);
    });
    return e.promise;
  }

  function pump() {
    while (active < CONCURRENCY && queue.length) {
      var h = queue.shift();
      active++;
      if (active > maxActive) maxActive = active;
      load(h).then(finish, finish);
    }
  }
  function finish() { active--; pump(); }

  /* LRU eviction keeps at most MAX_RETAINED parsed sections; never the displayed
     colour or the one the shopper came from. Evicted colours load normally on tap. */
  function evictBeyondCap() {
    var held = [];
    cache.forEach(function (e) { if (e.doc) held.push(e); });
    while (held.length > MAX_RETAINED) {
      var victims = held.filter(function (e) { return e.handle !== st.handle && e.handle !== st.prevHandle && e.status !== 'loading'; });
      if (!victims.length) break;
      victims.sort(function (a, b) { return (a.used || 0) - (b.used || 0); });
      var v = victims[0];
      v.doc = null; v.product = null; v.hero = null; v.heroImg = null;
      v.status = 'evicted';
      held.splice(held.indexOf(v), 1);
    }
  }

  /* exempt: the origin colour of a switch (at most one per visited colour); it does
     not consume the sibling budget, memory stays bounded by evictBeyondCap(). */
  function enqueue(h, front, exempt) {
    if (!h || h === st.handle) return;
    var e = cache.get(h);
    if (e && (e.status === 'loading' || (e.status === 'ready' && !isStale(e)))) return;
    if (e && e.status === 'evicted' && !exempt) return;
    var i = queue.indexOf(h);
    if (i !== -1) {
      if (front && i > 0) { queue.splice(i, 1); queue.unshift(h); }
      return;
    }
    if (!budgeted.has(h)) {
      if (exempt) {
        exemptFetches++;
      } else {
        if (budgeted.size >= MAX_PREFETCH) return;
        budgeted.add(h);
      }
    }
    if (front) queue.unshift(h); else queue.push(h);
    pump();
  }

  function siblingHandles() {
    var seen = [];
    PA('.productpage_color .pdp-color-option').forEach(function (c) {
      var h = cardHandle(c);
      if (h && seen.indexOf(h) === -1) seen.push(h);
    });
    return seen;
  }

  function startBulk() {
    st.bulkStarted = true;
    var c = navigator.connection;
    if (c && (c.saveData || /2g$/.test(c.effectiveType || ''))) { st.bulkAllowed = false; return; }
    siblingHandles().forEach(function (h) { enqueue(h, false); });
  }

  function scheduleBulk() {
    var go = function () {
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(startBulk, { timeout: 1500 });
      else setTimeout(startBulk, 200);
    };
    if (document.readyState === 'complete') go(); else window.addEventListener('load', go, { once: true });
  }

  function onIntent(ev) {
    var t = ev.target;
    if (!t || !t.closest) return;
    var card = t.closest('.productpage_color .pdp-color-option');
    if (!card || !inPdp(card)) return;
    var h = cardHandle(card);
    if (h && h !== st.handle) enqueue(h, true);
  }

  function onVisible() {
    if (document.visibilityState !== 'visible') return;
    cache.forEach(function (e, h) { if (e.status === 'ready' && isStale(e)) enqueue(h, false); });
  }

  /* ------------------------------------------------------- fallback rules */

  /* Rules evaluable before any fetch. Returns the rule id, or null. */
  function liveBlocked() {
    if (!st.enabled) return 'F17';
    if (!window.Shopify || typeof window.Shopify.OptionSelectors !== 'function') return 'F13';
    if (!P('.product-page--root[data-section-id]')) return 'F15';
    if (window.StampedFn && (typeof window.StampedFn.loadWidget !== 'function' || typeof window.StampedFn.loadBadges !== 'function')) return 'F16';
    var pj = window.product_json;
    if (!pj || pj.available === false) return 'F5';
    var gallery = P('.product-page--images');
    if (!gallery) return 'F10';
    if (gallery.querySelector(MEDIA_BLOCK)) return 'F6';
    if (hasTag(pj, 'unisex')) return 'F7';
    if (P('.outfit-left-slider')) return 'F12';
    return null;
  }

  /* Shop the Look integration (INTEGRATION-CONTRACT-STL.md, contract 1). The colour switch
     never touches STL's own nodes: STL tears down and rebuilds itself through its API. The
     live dialog is looked up in the PDP section first, then page-wide, so a dialog placed
     elsewhere (contract R1) is seen and makes the switch fall back to a normal load. */
  var STL_DIALOG = '[data-mdv-stl-dialog]';
  function liveStlDialog() { return P(STL_DIALOG) || document.querySelector(STL_DIALOG); }
  function stlApi() {
    var api = window.MdvShopTheLook;
    if (!api || api.contract !== 1 || typeof api.canSwap !== 'function' || typeof api.destroy !== 'function' || typeof api.init !== 'function') return null;
    return api;
  }
  /* F18: if either side has an STL dialog, the API must be present (contract 1) and agree.
     Amendment 1 item 4: no live dialog + a fetched dialog always loads normally, whatever
     the API says (the API can stay loaded after an earlier STL visit). */
  function stlBlocked(doc, via) {
    var fDlg = doc.querySelector(STL_DIALOG);
    var lDlg = liveStlDialog();
    if (!fDlg && !lDlg) return null;
    if (fDlg && !lDlg) return 'F18';
    var api = stlApi();
    if (!api) return 'F18';
    var ok = false;
    try {
      ok = api.canSwap({ root: P('.product-page--root'), dialog: lDlg || null, nextHasStl: !!fDlg, via: via }) === true;
    } catch (err) { ok = false; }
    return ok ? null : 'F18';
  }

  function targetBlocked(h, e, via) {
    var p = e.product;
    var doc = e.doc;
    if (h === 'gift-cards' || p.handle === 'gift-cards') return 'F9';
    if (p.available === false) return 'F5';
    if (Array.isArray(p.media) && p.media.some(function (m) { return m.media_type !== 'image'; })) return 'F6';
    var fImages = doc.querySelector('.product-page--images');
    if (fImages && fImages.querySelector(MEDIA_BLOCK)) return 'F6';
    if (hasTag(p, 'unisex')) return 'F7';
    if ((p.vendor || '') !== ((window.product_json && window.product_json.vendor) || '')) return 'F8';
    if (doc.querySelector('.outfit-left-slider')) return 'F12';
    return stlBlocked(doc, via || 'tap');
  }

  var REQUIRED = [
    '.product-page--root[data-section-id]',
    '.product-page--images-container .main-litght-img',
    '.main-thumbnail-slider',
    '.product-page-mobile--images-container .main-litght-img-mobile',
    '.image-zoom-new',
    '.product-page--description',
    '.product-page--title-n-vendor',
    '.prices',
    '.product-page--cart-form form .swatch',
    PDP_SELECT + ' option',
    '.variants',
    '#add',
    '.add-to-cart',
    '[data-mdv-variant-qty]',
    '#stamped-main-widget',
    '.stamped-main-badge'
  ];

  /* F10. Fetched and live sections must both carry every region the commit touches. */
  function validate(e) {
    var doc = e.doc;
    for (var i = 0; i < REQUIRED.length; i++) {
      if (!doc.querySelector(REQUIRED[i])) return 'F10:fetched ' + REQUIRED[i];
      if (!P(REQUIRED[i])) return 'F10:live ' + REQUIRED[i];
    }
    var id = String(e.product.id);
    if (doc.querySelector('#stamped-main-widget').getAttribute('data-product-id') !== id) return 'F10:widget id';
    var fRoot = doc.querySelector('.product-page--root');
    if (fRoot.getAttribute('data-product-id') !== id) return 'F10:root id';
    return null;
  }

  /* ------------------------------------------------------ commit helpers */

  function money(cents) {
    try {
      var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: cur }).format((cents || 0) / 100);
    } catch (e) { return String((cents || 0) / 100); }
  }

  /* Klaviyo `item`, read from the fetched section's own server-rendered script
     (section :779-788) so the values match a fresh load exactly. */
  var KLAVIYO_KEYS = /^(\s*)(Name|ProductID|Categories|ImageURL|URL|Brand|Price|CompareAtPrice)\s*:/gm;
  function klaviyoItem(doc, product) {
    var scripts = doc.querySelectorAll('script');
    for (var i = 0; i < scripts.length; i++) {
      var txt = scripts[i].textContent || '';
      if (txt.indexOf("'Viewed Product', item") === -1) continue;
      var m = /var item = (\{[\s\S]*?\n\s*\});/.exec(txt);
      if (!m) break;
      try { return JSON.parse(m[1].replace(KLAVIYO_KEYS, '$1"$2":')); } catch (err) { break; }
    }
    return {
      Name: product.title,
      ProductID: product.id,
      Categories: [],
      ImageURL: product.featured_image ? 'https:' + String(product.featured_image).replace(/^https?:/, '') : '',
      URL: location.origin + '/products/' + product.handle,
      Brand: product.vendor,
      Price: money(product.price),
      CompareAtPrice: money(product.compare_at_price_max)
    };
  }

  /* Re-applies what snippets/swatch.liquid's per-variant inline scripts do on a
     fresh load (they are stripped from transplanted markup): available/soldout
     classes per option value, and data-VID on Size values (last variant wins). */
  function applySwatchScripts(form, product) {
    var names = (product.options || []).map(function (o) { return (o && typeof o === 'object') ? o.name : o; });
    names.forEach(function (name, i) {
      var sw = form.querySelector('.swatch[data-option-index="' + i + '"]');
      if (!sw) return;
      var els = qa('.swatch-element', sw);
      product.variants.forEach(function (v) {
        var val = v.options[i];
        els.forEach(function (el) {
          if (el.getAttribute('data-value') !== val) return;
          if (v.available) {
            el.classList.remove('soldout');
            el.classList.add('available');
            qa('input[type="radio"]', el).forEach(function (r) { r.removeAttribute('disabled'); });
          }
          if (String(name).indexOf('Size') !== -1) {
            var pick = el.getAttribute('data-onlypick');
            PA('.swatch-element').forEach(function (x) {
              if (x.getAttribute('data-onlypick') === pick) x.setAttribute('data-VID', String(v.id));
            });
          }
        });
      });
    });
  }

  /* Recs (mdvRecsV1): an in-flight load for the previous product must not paint
     over the new one. Sequence token + wait for the running mount to settle. */
  function remountRecs(F) {
    var R = P('#product-recommendations-section');
    var FR = F.querySelector('#product-recommendations-section');
    var api = window.MdvRecsV1;
    if (!R || !FR || !api || typeof api.mountPdp !== 'function') return;
    copyAttrs(R, FR, ['data-mdv-recs-handle', 'data-mdv-recs-vendor', 'data-mdv-recs-title', 'data-mdv-recs-skus']);
    var token = ++st.recsSeq;
    var go = function () {
      if (token !== st.recsSeq) return;
      var sw = R.querySelector('.mdv-recs-swiper');
      if (sw && sw.swiper && typeof sw.swiper.destroy === 'function') sw.swiper.destroy(true, true);
      var track = R.querySelector('.mdv-recs-track');
      if (track) track.innerHTML = '';
      R.removeAttribute('data-mdv-recs-state');
      R.removeAttribute('data-mdv-recs-tier');
      R.classList.remove('mdv-recs--ready');
      R.style.display = '';
      api.mountPdp(R);
    };
    if (R.getAttribute('data-mdv-recs-state') !== 'running') { go(); return; }
    R.style.display = 'none';
    var done = false;
    var settle = function () {
      if (done) return;
      done = true;
      mo.disconnect();
      clearTimeout(timer);
      go();
    };
    var mo = new MutationObserver(function () {
      if (R.getAttribute('data-mdv-recs-state') !== 'running') settle();
    });
    mo.observe(R, { attributes: true, attributeFilter: ['data-mdv-recs-state'] });
    var timer = setTimeout(settle, 10000);
  }

  function notifyVariantId() {
    var p = window.product_json;
    var listbox = pdpSelect();
    var sel = listbox ? String(listbox.value || '') : '';
    var v = p.variants.filter(function (x) { return String(x.id) === sel; })[0];
    if (v && !v.available) return v.id;
    var so = p.variants.filter(function (x) { return !x.available; })[0];
    return so ? so.id : (v ? v.id : p.variants[0].id);
  }

  /* Amendment 1, BIS option ii: the BIS app caches the page-load product, so after a
     swap any #BIS_trigger click loads /products/<handle>?variant=<id> normally. */
  function syncNotifyButton(product, F) {
    var atc = P('.add-to-cart');
    if (!atc) return;
    var triggers = PA('[id="BIS_trigger"]');
    triggers.forEach(function (b) { b.classList.remove('getnotify'); b.style.display = ''; });
    var own = P('[id="BIS_trigger"][data-mdv-notify]');
    var app = triggers.filter(function (b) { return !b.hasAttribute('data-bis-tag') && !b.hasAttribute('data-mdv-notify'); });
    var bisHidden = !!F.querySelector('#BIS_trigger[data-bis-tag]');
    var soldOut = product.variants.some(function (v) { return !v.available; });
    var need = soldOut && !bisHidden && !app.length;
    if (need && !own) {
      var caption = NOTIFY_CAPTION;
      try {
        if (window.BIS && typeof window.BIS.currentButtonCaption === 'function') caption = window.BIS.currentButtonCaption() || caption;
      } catch (e) {}
      var b = document.createElement('button');
      b.type = 'button';
      b.id = 'BIS_trigger';
      b.className = 'button';
      b.setAttribute('data-mdv-notify', '1');
      b.setAttribute('aria-label', caption);
      b.textContent = caption;
      atc.appendChild(b);
    } else if (!need && own) {
      own.parentNode.removeChild(own);
    }
  }

  function onBisCapture(ev) {
    if (!st.swapped) return;
    var t = ev.target && ev.target.closest ? ev.target.closest('[id="BIS_trigger"]') : null;
    if (!t || !inPdp(t)) return;
    ev.preventDefault();
    ev.stopImmediatePropagation();
    location.assign('/products/' + encodeURIComponent(st.handle) + '?variant=' + notifyVariantId());
  }

  /* Shopify.OptionSelectors resolves its select with document.getElementById at
     construction (replaceSelector, initDropdown) and again on popstate
     (selectVariantFromDropdown). Wishlist drawer items reuse id="variant-listbox" and
     come first in the DOM, so a page-wide lookup binds the wrong element. For the
     synchronous construction only, every other element carrying that id loses it and
     gets it back in finally (no paint or script can run in between); this instance's
     later dropdown lookup then reads its own bound select. Shopify's prototype, the
     page's own instance and the drawer markup are untouched. */
  function bindOptionSelectors(select) {
    if (!select) throw new Error('PDP variant-listbox missing');
    var others = qa('[id="variant-listbox"]').filter(function (el) { return el !== select; });
    var inst = null;
    others.forEach(function (el) { el.removeAttribute('id'); });
    try {
      if (document.getElementById('variant-listbox') !== select) throw new Error('variant-listbox does not resolve to the PDP select');
      inst = new window.Shopify.OptionSelectors('variant-listbox', {
        product: window.product_json,
        onVariantSelected: window.selectCallback,
        enableHistoryState: true
      });
    } finally {
      others.forEach(function (el) { el.setAttribute('id', 'variant-listbox'); });
    }
    if (!inst || inst.variantIdField !== select) throw new Error('OptionSelectors bound outside the PDP');
    inst.selectVariantFromDropdown = function (options) {
      var field = this.variantIdField;
      var option = field && field.querySelector('[selected]');
      if (!option) return false;
      return this.selectVariant(option.value, options);
    };
    return inst;
  }

  /* Every OptionSelectors created with enableHistoryState registers a permanent popstate
     listener (HistoryState.register). After a switch, superseded instances (the page's
     own and earlier switches') would still react to Back/Forward and check radios in
     the live form while the PDP select keeps its own value. Instances are recorded
     through a wrapper installed at init (enabled pages only; behaviour unchanged) and
     every superseded one is made inert: its popstate entry point becomes a no-op. */
  function captureSelectorInstances() {
    var HS = window.Shopify && window.Shopify.OptionSelectors && window.Shopify.OptionSelectors.HistoryState;
    if (!HS || !HS.prototype || typeof HS.prototype.register !== 'function' || HS.prototype.__mdvColourSwap) return;
    var register = HS.prototype.register;
    HS.prototype.register = function (optionSelector) {
      st.selectorInstances.push(optionSelector);
      return register.apply(this, arguments);
    };
    HS.prototype.__mdvColourSwap = true;
  }

  function retireSupersededSelectors(current) {
    st.selectorInstances.forEach(function (inst) {
      if (inst === current || inst.__mdvRetired) return;
      inst.selectVariantFromParamsOrDropdown = function () {};
      inst.__mdvRetired = true;
    });
    st.selectorInstances = [current];
  }

  /* The checked radios (and the SKU line) mirror the PDP select, as the section's
     selectCallback does, but scoped to the PDP form. */
  function syncRadiosToSelect() {
    var select = pdpSelect();
    var pj = window.product_json;
    if (!select || !pj) return;
    var v = (pj.variants || []).filter(function (x) { return String(x.id) === String(select.value); })[0];
    if (!v) return;
    var form = select.form || select.closest('form');
    (v.options || []).forEach(function (val, i) {
      var want = String(val).replace(/"/g, '');
      qa('.swatch[data-option-index="' + i + '"] input[type="radio"]', form).forEach(function (r) {
        if (r.value === want) r.checked = true;
      });
    });
    var sku = P('[data-product-variant-sku]');
    if (sku) {
      var short = v.sku ? String(v.sku).trim().split('-').slice(0, 2).join('-') : '';
      sku.textContent = short;
      sku.hidden = !short;
    }
  }

  function markCurrent(h) {
    PA('.productpage_color .pdp-color-option').forEach(function (c) {
      c.classList.toggle('is-current', cardHandle(c) === h);
    });
  }

  function callHook(name, arg) {
    if (typeof window[name] === 'function') window[name](arg);
  }

  /* ---------------------------------------------------------------- commit */

  /* Synchronous and all-or-nothing after validation (I2). Throws on any problem;
     the caller turns a throw into a normal page load. */
  function commit(h, e, opts) {
    var $ = window.jQuery;
    var F = e.doc;
    var product = e.product;
    var norm = normalise(product);
    var prevHandle = st.handle;
    var fv = firstAvailable(product);
    var root = P('.product-page--root[data-section-id]');
    var via = opts.push ? 'tap' : 'popstate';

    /* 0. Shop the Look: re-check F18 synchronously (the dialog may have opened since the
       tap), then let STL tear itself down before any DOM is transplanted. */
    if (stlBlocked(F, via)) throw new Error('F18 Shop the Look cannot switch now');
    /* Placement (contract step 3, checked here before any DOM change so a refusal never
       leaves a half-switched page): every STL dialog, live or fetched, is a direct child
       of .product-page--cart-form. */
    var lCartForm = P('.product-page--cart-form');
    var fCartForm = F.querySelector('.product-page--cart-form');
    qa(STL_DIALOG).forEach(function (d) { if (!lCartForm || d.parentNode !== lCartForm) throw new Error('STL dialog is not a direct child of the cart form'); });
    qa(STL_DIALOG, F).forEach(function (d) { if (!fCartForm || d.parentNode !== fCartForm) throw new Error('fetched STL dialog is not a direct child of the cart form'); });
    var stlApiRef = stlApi();
    var lStlDlg = liveStlDialog();
    var hadStl = !!lStlDlg;
    if (lStlDlg) stlApiRef.destroy(lStlDlg);

    /* 1. Globals */
    window.product_json = norm;
    window.product_title = escapeHtml(product.title);
    window.product_variant_size = product.variants.length;
    window.product_options_first = escapeHtml(norm.options[0] || '');
    window.variant_ids = product.variants.map(function (v) {
      return [String(v.id), String(v.available), v.option1 == null ? '' : String(v.option1),
        v.option2 == null ? '' : String(v.option2), v.option3 == null ? '' : String(v.option3)];
    });
    window._BISConfig = window._BISConfig || {};
    var bisProduct = normalise(product);
    bisProduct.variants.forEach(function (v) {
      var qty = F.querySelector('[data-mdv-variant-qty] #variant_qty_' + v.id);
      v.inventory_quantity = qty ? (parseInt(qty.textContent, 10) || 0) : 0;
    });
    window._BISConfig.product = bisProduct;
    if (window.wsapps) {
      window.wsapps.product_id = String(product.id);
      window.wsapps.variant_id = String(fv.id);
      window.wsapps.product_handle = product.handle;
    }
    var kItem = klaviyoItem(F, product);
    window.item = kItem;

    /* 2. Gallery */
    var holder = P('.product-sec-holder');
    setClass(holder, F.querySelector('.product-sec-holder'), 'product-video-first');
    var desk = P('.product-page--images-container');
    replaceChildrenFrom(desk, F.querySelector('.product-page--images-container'));
    desk.scrollTop = 0;
    replaceChildrenFrom(P('.main-thumbnail-slider'), F.querySelector('.main-thumbnail-slider'));

    /* Carousels are torn down and refilled here; they are re-initialised after all
       DOM writes (see "Inits") so the browser recalculates style/layout once. */
    var mob = P('.product-page-mobile--images-container');
    var $mob = $(mob);
    var mobHeight = mob.offsetHeight;
    if (mobHeight) mob.style.minHeight = mobHeight + 'px';
    if ($mob.hasClass('slick-initialized')) $mob.slick('unslick');
    replaceChildrenFrom(mob, F.querySelector('.product-page-mobile--images-container'));

    var zoom = P('.image-zoom-new');
    var $zoom = (window.$zoom_slider && window.$zoom_slider.length) ? window.$zoom_slider : $(zoom);
    if ($zoom.hasClass('slick-initialized')) $zoom.slick('unslick');
    replaceChildrenFrom(zoom, F.querySelector('.image-zoom-new'));

    /* 3. Description block */
    var D = P('.product-page--description');
    var FD = F.querySelector('.product-page--description');
    setClass(D, FD, 'product-info-updates');
    setClass(D, FD, 'no-image');
    syncPart(D, FD, '.prod-icon-img-main', function (n) { D.insertBefore(n, D.firstChild); });
    syncPart(D, FD, '.collection-product-tag', function (n) {
      var icon = directChild(D, '.prod-icon-img-main');
      if (icon) icon.after(n); else D.insertBefore(n, D.firstChild);
    });
    syncPart(D, FD, '.product-page--title-n-vendor', function (n) {
      var tag = directChild(D, '.collection-product-tag');
      if (tag) tag.after(n); else D.insertBefore(n, D.firstChild);
    });
    syncPart(D, FD, '.product_information', function (n) { directChild(D, '.product-page--title-n-vendor').after(n); });
    syncPart(D, FD, '.prices', function (n) {
      (directChild(D, '.product_information') || directChild(D, '.product-page--title-n-vendor')).after(n);
    });

    var CF = directChild(D, '.product-page--cart-form');
    var FCF = directChild(FD, '.product-page--cart-form');
    var form = directChild(CF, 'form');
    var fForm = directChild(FCF, 'form');
    if (!CF || !FCF || !form || !fForm) throw new Error('cart form missing');
    /* custom.js updateDeliveryContentWithAjax() rewrites .update-delivery-content for the
       shopper's country after load. That text is per country, not per product: carry
       the live version into the transplanted tabs (a pending request still updates all). */
    var liveDelivery = CF.querySelector('.update-delivery-content');
    var deliveryHtml = liveDelivery ? liveDelivery.innerHTML : null;

    syncPart(CF, FCF, '.sell_price_text', function (n) { form.after(n); });
    var afterSell = function () { return directChild(CF, '.sell_price_text') || form; };
    syncPart(CF, FCF, '.product_recommendations', function (n) { afterSell().after(n); });
    syncPart(CF, FCF, '.product-single__store-availability-container', function (n) {
      (directChild(CF, '.product_recommendations') || afterSell()).after(n);
    });
    var store = directChild(CF, '.product-single__store-availability-container');
    var tabSel = '.product_tabs_new.hide-only-for-mob, .product_tabs';
    var oldTabs = directChild(CF, tabSel);
    if (oldTabs) oldTabs.parentNode.removeChild(oldTabs);
    var fTabs = directChild(FCF, tabSel);
    if (fTabs) {
      var newTabs = importEl(fTabs, true);
      if (store) store.after(newTabs); else afterSell().after(newTabs);
    }
    var stl = directChild(CF, '.new-shop-the-look');
    syncPart(CF, FCF, '.product_tabs_popup', function (n) {
      if (stl) CF.insertBefore(n, stl); else CF.appendChild(n);
    }, true);
    if (deliveryHtml !== null) {
      qa('.update-delivery-content', CF).forEach(function (el) { el.innerHTML = deliveryHtml; });
    }
    Array.prototype.slice.call(CF.children).forEach(function (c) {
      if (c.matches('style[data-mdv-stl-style]')) CF.removeChild(c);
    });
    stl = syncPart(CF, FCF, '.new-shop-the-look', function (n) { CF.appendChild(n); });
    var stlStyles = document.createDocumentFragment();
    Array.prototype.forEach.call(FCF.children, function (c) {
      if (c.matches('style[data-mdv-stl-style]')) stlStyles.appendChild(document.importNode(c, true));
    });
    if (stl) CF.insertBefore(stlStyles, stl); else CF.appendChild(stlStyles);
    /* 3b. Shop the Look dialog (placement verified at step 0): transplanted as a direct
       child of .product-page--cart-form, after .new-shop-the-look, else appended. */
    var stlDlg = syncPart(CF, FCF, STL_DIALOG, function (n) {
      var row = directChild(CF, '.new-shop-the-look');
      if (row) row.after(n); else CF.appendChild(n);
    }, true);

    /* 4. Form */
    if (fForm.getAttribute('id')) form.setAttribute('id', fForm.getAttribute('id'));
    var pc = directChild(form, '.productpage_color');
    if (!pc) throw new Error('colour carousel missing');
    var sale = directChild(form, '#_saleitem');
    var fSale = directChild(fForm, '#_saleitem');
    if (fSale && !sale) pc.before(importEl(fSale));
    if (!fSale && sale) sale.parentNode.removeChild(sale);
    Array.prototype.slice.call(form.children).forEach(function (c) {
      if (c.matches('.swatch, style[data-mdv-swatch-style]')) form.removeChild(c);
    });
    var swFrag = document.createDocumentFragment();
    Array.prototype.forEach.call(fForm.children, function (c) {
      if (c.matches('.swatch')) swFrag.appendChild(importEl(c));
      else if (c.matches('style[data-mdv-swatch-style]')) swFrag.appendChild(document.importNode(c, true));
    });
    pc.after(swFrag);
    applySwatchScripts(form, product);

    var qtyLive = form.querySelector('[data-mdv-variant-qty]');
    qtyLive.parentNode.replaceChild(importEl(fForm.querySelector('[data-mdv-variant-qty]')), qtyLive);
    qa('.pdp.check-stock-msg', form).forEach(function (m) { m.innerHTML = ''; m.classList.remove('active'); });
    var please = form.querySelector('#pleaseselectsize');
    if (please) please.classList.add('hide');
    var variants = form.querySelector('.variants');
    var fVariants = fForm.querySelector('.variants');
    replaceChildrenFrom(variants, fVariants);
    variants.className = fVariants.className;

    var add = form.querySelector('#add');
    var fAdd = fForm.querySelector('#add');
    add.setAttribute('value', fAdd.getAttribute('value') || '');
    add.value = fAdd.getAttribute('value') || '';
    add.disabled = fAdd.hasAttribute('disabled');
    var txt = form.querySelector('#text-addtocart');
    var fTxt = fForm.querySelector('#text-addtocart');
    if (txt && fTxt) txt.innerHTML = fTxt.innerHTML;
    var onClick = form.querySelector('#onClickContent');
    var fOnClick = fForm.querySelector('#onClickContent');
    if (onClick && fOnClick) onClick.innerHTML = fOnClick.innerHTML;
    var pre = form.querySelector('#PreOrder');
    var fPre = fForm.querySelector('#PreOrder');
    if (pre && fPre) {
      pre.checked = fPre.hasAttribute('checked');
      if (pre.checked) pre.setAttribute('checked', 'checked'); else pre.removeAttribute('checked');
    }
    var atc = form.querySelector('.add-to-cart');
    var fAtc = fForm.querySelector('.add-to-cart');
    setClass(atc, fAtc, 'hideTimer');
    setClass(atc, fAtc, 'show-only-select-size');
    var themeBis = atc.querySelector('#BIS_trigger[data-bis-tag]');
    var fThemeBis = fAtc.querySelector('#BIS_trigger[data-bis-tag]');
    if (fThemeBis) {
      var nb = importEl(fThemeBis);
      if (themeBis) themeBis.parentNode.replaceChild(nb, themeBis); else atc.appendChild(nb);
    } else if (themeBis) {
      themeBis.parentNode.removeChild(themeBis);
    }
    PA('.add-to-cart-wishlist').forEach(function (w) { w.classList.remove('single_wishlist', 'pre-order-content'); });
    CF.classList.remove('whishlist-full');
    var left = P('.product-page-left-sec');
    if (left) left.classList.remove('recm-view');
    var pickup = form.querySelector('#onlypickup');
    if (pickup) pickup.value = '';
    /* $('.addToCartBtn').show() without a forced style read: clear the inline hide. */
    PA('.addToCartBtn').forEach(function (el) { if (el.style.display === 'none') el.style.display = ''; });
    try { syncNotifyButton(product, F); } catch (err) {}

    /* 6. Stamped (Amendment 1: loadWidget(true) + loadBadges) */
    var W = P('#stamped-main-widget');
    var FW = F.querySelector('#stamped-main-widget');
    copyAttrs(W, FW, ['data-product-id', 'data-name', 'data-url', 'data-image-url', 'data-description', 'data-product-sku']);
    var B = P('.stamped-main-badge');
    var FB = F.querySelector('.stamped-main-badge');
    copyAttrs(B, FB, ['data-id', 'data-product-sku', 'data-product-title']);
    root.setAttribute('data-product-id', String(product.id));
    [W, B].forEach(function (el) { el.style.display = 'none'; el.style.opacity = '0'; });
    while (B.firstChild) B.removeChild(B.firstChild);

    /* 9. Wishlist (markup + attributes; the API refresh runs after paint) */
    var ws = P('#ws-fav-html');
    var fws = F.querySelector('#ws-fav-html');
    if (ws && fws) {
      copyAttrs(ws, fws, ['data-varid', 'data-handle', 'data-categoryid']);
      ws.removeAttribute('data-varid-dddd');
      ws.classList.remove('ws-active');
      replaceChildrenFrom(ws, fws);
      /* The server markup draws a filled star; cirkle-wishlist.js normalises it on
         page load with wishlistBtnPDP() (no argument: empty icon, no ws-active).
         Do the same now so a filled star never paints; logged-in state is fetched
         after paint (afterPaint), mirroring the script's page-load branching. */
      if (typeof window.wishlistBtnPDP === 'function') {
        try { window.wishlistBtnPDP(); } catch (err) {}
      }
    }

    /* Inits, after every DOM write above. */
    /* 2'. Mobile and zoom carousels (section :424-429 and :591-597 options) */
    $mob.slick({ slidesToShow: 1, infinite: true, dots: true, arrows: false });
    if (mobHeight) {
      var clearMin = function () { mob.style.minHeight = ''; };
      var heroEl = mob.querySelector('.slick-current img') || mob.querySelector('.main-litght-img-mobile img');
      if (heroEl && !heroEl.complete) {
        heroEl.addEventListener('load', clearMin, { once: true });
        heroEl.addEventListener('error', clearMin, { once: true });
      } else {
        requestAnimationFrame(clearMin);
      }
      setTimeout(clearMin, 1000);
    }
    $zoom.slick({ slidesToShow: 1, infinite: false, fade: true, dots: false, arrows: true });

    /* 5. Variant selection */
    st.optionSelectors = bindOptionSelectors(pdpSelect());
    retireSupersededSelectors(st.optionSelectors);
    /* $('.selector-wrapper').hide() without a forced style read. */
    PA('.selector-wrapper').forEach(function (el) { el.style.display = 'none'; });

    /* 7. Selected for You */
    remountRecs(F);

    /* 8. Re-init hooks exposed by the theme (size guide refresh runs after paint) */
    callHook('mdvInitShopTheLook');
    opts.stl = hadStl ? 'removed' : 'none';
    if (stlDlg) {
      var stlReady = false;
      try {
        stlReady = stlApiRef.init(stlDlg) === true;
      } catch (err) {
        try { stlApiRef.destroy(stlDlg); } catch (err2) {}
        throw err;
      }
      opts.stl = (stlReady && stlDlg.isConnected) ? 'reinit' : 'removed';
    }
    callHook('mdvProductTabsRefresh');
    callHook('mdvRecReset');
    callHook('mdvStampedReconcile');
    if (typeof window.adjustDivHeight === 'function') window.adjustDivHeight('.product-page-left-sec', '#thumbnail-item-1');

    /* I1: ATC correctness, asserted before history is touched. */
    var ids = window.product_json.variants.map(function (v) { return String(v.id); });
    var select = pdpSelect();
    var opts2 = select ? qa('option', select).map(function (o) { return o.value; }) : [];
    if (!opts2.length || opts2.some(function (v) { return ids.indexOf(v) === -1; })) throw new Error('I1 variant-listbox');
    if (!st.optionSelectors || st.optionSelectors.variantIdField !== select) throw new Error('I1 OptionSelectors not bound to the PDP select');
    var rid = root.getAttribute('data-product-id');
    if (String(window.product_json.id) !== rid || rid !== W.getAttribute('data-product-id')) throw new Error('I1 product id');
    /* I6: at most one STL dialog; a connected one is ready for this product; none -> no STL buttons. */
    var stlDialogs = PA(STL_DIALOG);
    if (stlDialogs.length > 1) throw new Error('I6 more than one STL dialog');
    if (stlDialogs.length === 1) {
      if (stlDialogs[0].dataset.mdvStlReady !== 'true' || stlDialogs[0].dataset.parentProductId !== String(product.id)) throw new Error('I6 STL dialog not ready for this product');
    } else if (P('[data-mdv-stl-open]')) {
      throw new Error('I6 STL open buttons without a dialog');
    }

    /* 10. Displayed product. The history entry itself is written right after the first
       paint (afterPaint): third-party pushState wrappers cost ~45 ms at 4x CPU. A Back /
       Forward commit drops ?variant= now, so URL, select and radio agree immediately. */
    st.prevHandle = prevHandle;
    st.handle = product.handle;
    st.swapped = true;
    e.used = Date.now();
    if (!opts.push && /[?&]variant=/.test(location.search)) {
      history.replaceState({ mdvColourSwap: { handle: product.handle, prev: prevHandle } }, '', stripVariant(location.href));
    }
    syncRadiosToSelect();

    /* 11. Current card */
    markCurrent(product.handle);

    mark('mdv-swap-commit');
    st.stampedExpect = String(product.id);
    st.stampedRetries = 0;

    /* After the first paint of the new product: work that is off-screen or not
       visual. Each step is isolated so a third-party failure cannot undo a swap
       whose DOM is already consistent. */
    var swapId = st.tapSeq;
    var tapAt = opts.tapAt;
    var tailDone = false;
    var tail = function () {
      if (tailDone) return;
      tailDone = true;
      afterPaint(product, F, kItem, fv, ws, prevHandle, swapId, opts);
    };
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        mark('mdv-swap-painted');
        var painted = now();
        try { performance.measure('mdv-swap', 'mdv-swap-tap', 'mdv-swap-painted'); } catch (err) {}
        window.__mdvSwapLast = {
          handle: product.handle,
          ms: Math.round((painted - tapAt) * 10) / 10,
          cached: !!opts.cached,
          via: opts.push ? 'tap' : 'popstate'
        };
        tail();
      });
    });
    setTimeout(tail, 1000); /* rAF does not run in background tabs */
  }

  function afterPaint(product, F, kItem, fv, ws, prevHandle, swapId, opts) {
    var current = st.handle === product.handle && st.tapSeq === swapId;
    /* 10. History: one entry per switch (D2), written in commit order even if a newer
       switch has already been committed. */
    try {
      var swapState = { mdvColourSwap: { handle: product.handle, prev: prevHandle } };
      if (opts.push) history.pushState(swapState, '', '/products/' + product.handle);
      else history.replaceState(swapState, '', stripVariant(location.href));
      if (current && product.variants.length === 1) {
        var only = P('.size_swatch input[type=radio]');
        if (only) only.click();
      }
      if (current && (/[?&]variant=/.test(location.search) || !(history.state && history.state.mdvColourSwap))) {
        history.replaceState(swapState, '', stripVariant(location.href));
      }
      if (current && st.titleShown && document.title.indexOf(st.titleShown) !== -1) {
        document.title = document.title.replace(st.titleShown, product.title);
        st.titleShown = product.title;
      }
    } catch (err) {}
    if (current) {
      try { callHook('mdvSizeGuideRefresh', F); } catch (err) {}
      if (window.StampedFn) {
        try { window.StampedFn.loadWidget(true); window.StampedFn.loadBadges(); } catch (err) {}
      }
      /* Login state as the wishlist script sees it: ajaxcustomer_id (global var in
         cirkle-wishlist.js) is also set by a popup login without a reload, unlike
         window.wsapps.customerId. Logged out: nothing more (commit already matched
         the logged-out page-load state). */
      var wishlistCustomer = typeof ajaxcustomer_id !== 'undefined' && ajaxcustomer_id;
      if (ws && wishlistCustomer && typeof window.CSRetrieveWishlistProData === 'function') {
        try { window.CSRetrieveWishlistProData(); } catch (err) {}
      }
      try { window.dispatchEvent(new Event('resize')); } catch (err) {}
    }
    try {
      if (window.Shopify && Shopify.Products && typeof Shopify.Products.recordRecentlyViewed === 'function') Shopify.Products.recordRecentlyViewed();
    } catch (err) {}
    try {
      window._learnq = window._learnq || [];
      window._learnq.push(['track', 'Viewed Product', kItem]);
      window._learnq.push(['trackViewedItem', {
        Title: kItem.Name, ItemId: kItem.ProductID, Categories: kItem.Categories, ImageUrl: kItem.ImageURL, Url: kItem.URL,
        Metadata: { Brand: kItem.Brand, Price: kItem.Price, CompareAtPrice: kItem.CompareAtPrice }
      }]);
    } catch (err) {}
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('mdv_colour_switched', {
          productId: product.id,
          handle: product.handle,
          title: product.title,
          variantId: fv.id,
          price: product.price / 100,
          url: location.origin + '/products/' + product.handle
        });
      }
    } catch (err) {}
    if (current) syncRadiosToSelect();
    /* Informational (contract: cannot veto). Fires after the history change on both paths
       (tap: pushState above; popstate: the URL already changed), per amendment 1 item 1. */
    try {
      var sel = pdpSelect();
      document.dispatchEvent(new CustomEvent('mdv:colour-switched', {
        detail: {
          handle: product.handle,
          prevHandle: prevHandle,
          productId: product.id,
          variantId: sel && sel.value ? Number(sel.value) : fv.id,
          via: opts.push ? 'tap' : 'popstate',
          root: P('.product-page--root'),
          stl: opts.stl || 'none'
        }
      }));
    } catch (err) {}
    if (prevHandle && prevHandle !== product.handle) enqueue(prevHandle, true, true);
  }

  function commitOrNavigate(h, e, opts, fallback) {
    try {
      commit(h, e, opts);
      return true;
    } catch (err) {
      try { console.debug('[mdvColourSwap] commit failed, loading normally', err); } catch (e2) {}
      fallback();
      return false;
    }
  }

  /* --------------------------------------------------------------- events */

  function tapTime(ev) {
    var t = ev && ev.timeStamp;
    var n = now();
    return (t > 0 && t <= n + 5) ? t : n;
  }

  function waitReady(h, e) {
    var deadline = now() + WAIT_INFLIGHT_MS;
    if (e.status === 'ready' && isStale(e)) enqueue(h, true);
    return new Promise(function (resolve) {
      (function check() {
        if (e.status === 'ready' && !isStale(e)) return resolve(true);
        if (e.status === 'failed') return resolve(false);
        var left = deadline - now();
        if (left <= 0) return resolve(false);
        var p = e.status === 'loading' ? e.promise : null;
        var t = setTimeout(check, p ? left : Math.min(left, 50));
        if (p) p.then(function () { clearTimeout(t); check(); });
      })();
    });
  }

  function onClick(ev) {
    var a = ev.target && ev.target.closest ? ev.target.closest('.productpage_color .pdp-color-option a') : null;
    if (!a || !inPdp(a)) return;
    if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return; /* F1, F2 */
    var h = cardHandle(a.closest('.pdp-color-option'));
    if (!h) return;
    if (h === st.handle) {
      /* Already displayed: once switched in place this card is the selected one. */
      if (st.swapped) ev.preventDefault();
      return;
    }
    if (h === 'gift-cards') return; /* F9 */
    if (liveBlocked()) return; /* F5 F6 F7 F12 F13 F15 F16 F17 */
    var href = a.href;
    ev.preventDefault();
    mark('mdv-swap-tap');
    var tapAt = tapTime(ev);
    var seq = ++st.tapSeq;
    markCurrent(h);
    var e = cache.get(h);
    if (!e || e.status === 'idle' || e.status === 'failed' || e.status === 'evicted') { location.assign(href); return; } /* F3, F14 */
    var cached = e.status === 'ready' && !isStale(e);
    var wait = cached ? Promise.resolve(true) : waitReady(h, e); /* F4 */
    wait.then(function (ok) {
      if (seq !== st.tapSeq) return;
      if (!ok || targetBlocked(h, e, 'tap') || validate(e)) { location.assign(href); return; }
      return within(e.hero, WAIT_HERO_MS).then(function () {
        if (seq !== st.tapSeq) return;
        if (liveBlocked()) { location.assign(href); return; }
        commitOrNavigate(h, e, { push: true, tapAt: tapAt, cached: cached }, function () { location.assign(href); });
      });
    });
  }

  function onPopState(ev) {
    var s = ev.state && ev.state.mdvColourSwap;
    var h = (s && s.handle) || handleFromPath(location.pathname);
    if (!h || h === st.handle) return;
    var e = cache.get(h);
    var tapAt = tapTime(ev);
    if (e && e.status === 'ready' && !isStale(e) && !liveBlocked() && !targetBlocked(h, e, 'popstate') && !validate(e)) {
      mark('mdv-swap-tap');
      st.tapSeq++;
      commitOrNavigate(h, e, { push: false, tapAt: tapAt, cached: true }, function () { location.reload(); });
    } else {
      location.reload();
    }
  }

  /* Stamped can ignore loadWidget while its own first load is still running. */
  function onStampedLoaded() {
    if (!st.stampedExpect) return;
    var w = P('#stamped-main-widget');
    var f = w && w.querySelector('form[id^="new-review-form_"]');
    if (f && f.getAttribute('id') === 'new-review-form_' + st.stampedExpect) { st.stampedExpect = null; return; }
    if (st.stampedRetries++ < 2 && window.StampedFn && typeof window.StampedFn.loadWidget === 'function') {
      setTimeout(function () { try { window.StampedFn.loadWidget(true); } catch (err) {} }, 0);
    }
  }

  function injectStyle() {
    var s = document.createElement('style');
    s.setAttribute('data-mdv-colour-swap', MARKER);
    s.textContent =
      '.productpage_color .pdp-color-option:not(.is-current) a{border-color:transparent}\n' +
      '.productpage_color .pdp-color-option:not(.is-current) a:hover{border-color:#66545154}\n' +
      '.productpage_color .pdp-color-option.is-current a{border-color:var(--brown)}\n';
    document.head.appendChild(s);
  }

  function observeCarousel() {
    var host = P('#pdp-color-main');
    if (!host || !('MutationObserver' in window)) return;
    new MutationObserver(function (records) {
      records.forEach(function (r) {
        Array.prototype.forEach.call(r.addedNodes, function (n) {
          if (n.nodeType !== 1 || !n.matches('.pdp-color-option')) return;
          var h = cardHandle(n);
          n.classList.toggle('is-current', h === st.handle);
          if (st.bulkStarted && st.bulkAllowed) enqueue(h, false);
        });
      });
    }).observe(host, { childList: true });
  }

  function init() {
    st.enabled = isEnabled();
    window.MdvColourSwap = {
      marker: MARKER,
      enabled: st.enabled,
      stats: function () {
        var entries = [];
        cache.forEach(function (e, h) { entries.push({ handle: h, status: e.status, at: e.at || null }); });
        var retained = 0;
        cache.forEach(function (e) { if (e.doc) retained++; });
        return { handle: st.handle, swapped: st.swapped, budgeted: budgeted.size, exemptFetches: exemptFetches, retained: retained, maxActive: maxActive, queued: queue.slice(), entries: entries, selectorInstances: st.selectorInstances.length };
      }
    };
    if (!st.enabled) return; /* F17: inert */
    var root = q('.product-page--root[data-section-id][data-product-id]');
    if (!root) return;
    st.section = root.closest('.shopify-section') || root;
    if (!P('.productpage_color') || !pdpSelect()) return;
    if (!window.Shopify || typeof window.Shopify.OptionSelectors !== 'function' || !window.jQuery || !window.product_json) return;
    captureSelectorInstances();
    st.sid = root.getAttribute('data-section-id');
    st.handle = window.product_json.handle;
    st.titleShown = window.product_json.title || '';
    var hs = (history.state && typeof history.state === 'object') ? history.state : {};
    history.replaceState(Object.assign({}, hs, { mdvColourSwap: { handle: st.handle } }), '', location.href);
    injectStyle();
    markCurrent(st.handle);
    document.addEventListener('click', onClick, false);
    document.addEventListener('touchstart', onIntent, { capture: true, passive: true });
    document.addEventListener('pointerenter', onIntent, { capture: true, passive: true });
    window.addEventListener('popstate', onPopState);
    window.addEventListener('click', onBisCapture, true);
    document.addEventListener('stamped:reviews:loaded', onStampedLoaded);
    document.addEventListener('visibilitychange', onVisible);
    observeCarousel();
    scheduleBulk();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
